import React, { useState } from 'react';
import {
  Box,
  HStack,
  Select,
  Input,
  Button,
  Text,
  useToast,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useUserLists } from './useUserLists';

const NEW_LIST_VALUE = '__NEW__';

/**
 * Sticky toolbar for analytics tables. Shows the selection count and an
 * "Add to list" picker (existing list or "+ New list…").
 *
 * Props:
 *   selectedUserIds   Set<string>
 *   onClearSelection  () => void  called after a successful add
 */
export function UserListBulkAddBar({ selectedUserIds, onClearSelection }) {
  const { lists, createList, addMembers } = useUserLists();
  const [target, setTarget] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  if (!selectedUserIds || selectedUserIds.size === 0) return null;

  const ids = Array.from(selectedUserIds);
  const isNewList = target === NEW_LIST_VALUE;
  const canSubmit =
    !busy && (target && (!isNewList || newName.trim().length > 0));

  const onAdd = async () => {
    setBusy(true);
    try {
      let listId = target;
      if (isNewList) {
        const created = await createList(newName);
        listId = created.id;
      }
      await addMembers(listId, ids);
      toast({
        title: `Added ${ids.length} user${ids.length === 1 ? '' : 's'}`,
        status: 'success',
        duration: 2000,
      });
      setTarget('');
      setNewName('');
      onClearSelection();
    } catch (e) {
      toast({
        title: 'Add to list failed',
        description: String(e.message || e),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      position="sticky"
      bottom={0}
      bg="white"
      borderTopWidth="1px"
      borderColor="gray.200"
      p={3}
      zIndex={2}
    >
      <HStack spacing={3} flexWrap="wrap">
        <Text fontWeight="medium">{ids.length} selected</Text>
        <FormControl w="220px">
          <FormLabel htmlFor="bulk-add-target" srOnly>Add to list</FormLabel>
          <Select
            id="bulk-add-target"
            placeholder="Add to list…"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          >
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
            <option value={NEW_LIST_VALUE}>+ New list…</option>
          </Select>
        </FormControl>
        {isNewList && (
          <Input
            placeholder="New list name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            w="200px"
          />
        )}
        <Button colorScheme="blue" onClick={onAdd} isDisabled={!canSubmit}>
          Add
        </Button>
        <Button variant="ghost" onClick={onClearSelection}>
          Clear
        </Button>
      </HStack>
    </Box>
  );
}
